import {
  codigoDeCupomSchema,
  criarItemDeWaitlistSchema,
  dayOfWeekFromKey,
  formatInTimeZone,
  fromWire,
  localDayKey,
  type Money,
  normalizarCodigoDeCupom,
  precificacao,
  ROTULO_DA_COBRANCA,
  ROTULO_DA_FAIXA,
  ROTULO_DA_ORIGEM_DO_AJUSTE,
  sumAll,
  TAMANHO_MAXIMO_DE_PAGINA,
} from '@locaveri/shared'
import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import {
  BellPlus,
  Calculator,
  CalendarCheck,
  CircleSlash,
  Handshake,
  LoaderCircle,
  Package,
  RefreshCw,
  Ruler,
  Send,
  TriangleAlert,
} from 'lucide-react'
import { type ReactElement, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

import { Campo } from '@/components/form/campo'
import {
  DialogoDeFecharReserva,
  type CotacaoParaFechar,
} from '@/features/reservas/dialogo-de-fechar-reserva'
import {
  DialogoDeLinkParaOCliente,
  type DadosDoLinkParaOCliente,
} from '@/features/reservas/link-para-o-cliente'
import { EmptyState } from '@/components/state/empty-state'
import { ErrorState } from '@/components/state/error-state'
import { LoadingState } from '@/components/state/loading-state'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { type Conta, type FiltrosDeContas, nomeDaConta, useContas } from '@/features/contas/api'
import { useCategoriasAtivas, useLocaisAtivos } from '@/features/frota/api'
import { rotuloDosDiasDaSemana } from '@/features/precos/api'
import { BadgeDeStatusDaCotacao, BadgeDeTipoDeLinha } from '@/features/precos/badges'
import {
  ACAO_DO_MOTIVO_DE_INDISPONIBILIDADE,
  type CategoriaCotada,
  disponibilidadeEmTexto,
  type ExtraDoPedido,
  type MotivoDeIndisponibilidade,
  type PedidoDeCotacao,
  type ResultadoDaCotacao,
  ROTULO_DO_MOTIVO_DE_INDISPONIBILIDADE,
  ROTULO_DO_MOTIVO_DE_RECUSA_DE_CUPOM,
  ROTULO_DO_MOTIVO_DE_RECUSA_DE_EXTRA,
  statusVigenteDaCotacao,
  useCotar,
  useCriarItemDeWaitlist,
} from '@/features/precos/cotacao'
import {
  campoDeInstanteRelativo,
  erroDoPeriodo,
  paraInstante,
} from '@/features/precos/dialogo-de-bloqueio'
import { apiGet, errorMessage } from '@/lib/api'
import { formatarData, formatarDataHora } from '@/lib/datas'
import { formatarMoedaDoWire } from '@/lib/dinheiro'
import { ROTAS_DE_VITRINE } from '@/lib/endpoints'
import { aplicarErrosDaApi, aplicarErrosDoZod } from '@/lib/formulario'
import { apenasDigitos, mascararTelefoneProgressivo } from '@/lib/mascaras'
import { usePermissao } from '@/lib/permissoes'

/**
 * Simulador de cotação — a tela que faz o gestor confiar na regra.
 *
 * ---------------------------------------------------------------------------
 * O PAINEL NÃO CALCULA PREÇO. ELE CONFERE.
 * ---------------------------------------------------------------------------
 * Invariante nº 3: quem decide preço é o motor determinístico do servidor. Aqui
 * não se soma diária, não se aplica multiplicador e não se estima total — o
 * número vem congelado no `QuoteSnapshot`, com o hash das regras que o
 * produziram, e a tela o EXIBE.
 *
 * A única aritmética monetária deste arquivo é uma CONFERÊNCIA: a soma das
 * linhas do breakdown tem que dar o total. Ela existe porque um breakdown que
 * não fecha é o pior defeito possível numa tela de preço — o cliente confere,
 * não bate, e nunca mais confia no número. Quando não fecha, a tela mostra o
 * ERRO no lugar do total: exibir um número que a própria tela sabe estar errado
 * é pior do que não exibir nada.
 *
 * As linhas SOMAM, não subtraem: desconto vem negativo do servidor. Uma tela que
 * subtraísse o cupom aplicaria o desconto duas vezes.
 *
 * ---------------------------------------------------------------------------
 * O DETALHE DIA A DIA É O QUE EXPLICA O TOTAL
 * ---------------------------------------------------------------------------
 * "Por que deu R$ 4.200?" não se responde com um número só. `porDia` traz cada
 * diária com a base, os ajustes que a sazonalidade e o dia da semana fizeram, e
 * se o teto ou o piso da categoria MORDEU o valor. O fim de semana e o feriado
 * pesando aparecem ali, linha por linha — e é essa tabela que transforma
 * "confia em mim" em "olha aqui".
 *
 * ---------------------------------------------------------------------------
 * INDISPONÍVEL NÃO É ERRO, E NUNCA É SILÊNCIO
 * ---------------------------------------------------------------------------
 * `POST /cotacoes` responde 201 mesmo quando nada está disponível: "não há
 * carro", "o cupom venceu" e "esta categoria não tem tarifa" são leitura do
 * mercado, não falha da requisição. Cada motivo vem nomeado, com a frase do que
 * fazer a respeito — e quando cabe lista de espera, a tela oferece registrar a
 * intenção em vez de encerrar a conversa com "indisponível".
 *
 * Extra recusado por falta de prestador aparece com o motivo, sempre: omiti-lo
 * faria o total ficar menor do que o cliente pediu, sem nada na tela dizendo por
 * quê — e é justamente esse o caso em que a locadora quer ligar oferecendo outra
 * data.
 *
 * ---------------------------------------------------------------------------
 * O AMARELO APARECE DUAS VEZES
 * ---------------------------------------------------------------------------
 * No botão "Cotar" e no TOTAL de cada categoria. É onde o olho do gestor precisa
 * parar. Faixa, tipo de linha, ajuste e caução são cinza; teto/piso e recusa são
 * âmbar e vermelho semânticos — amarelo aqui é marca, nunca aviso.
 */

// ---------------------------------------------------------------------------
// A vitrine de extras
// ---------------------------------------------------------------------------

/**
 * Os extras que a cotação aceita.
 *
 * É a VITRINE, e não a listagem administrativa: o recorte já vem só com os
 * ativos e sem os campos de administração — que é exatamente o conjunto que
 * pode entrar num pedido de cotação. Filtrar de novo no cliente seria repetir
 * uma regra que a API já aplica, e oferecer um extra desativado terminaria em
 * `INATIVO` na resposta.
 *
 * O schema mora aqui porque esta é a única tela que consome a vitrine; quando a
 * segunda aparecer, ele sobe para `features/precos`.
 */
const extraDaVitrineSchema = z.object({
  id: z.uuid(),
  nome: z.string(),
  descricao: z.string().nullable(),
  tipo: precificacao.tipoDeExtraSchema,
  cobranca: precificacao.cobrancaDeExtraSchema,
  /** Centavos como string decimal (o wire de `Money`). */
  valorUnitario: z.string(),
  franquia: z.string().nullable(),
  /** Teto por reserva. `null` = sem teto. */
  quantidadeMaxima: z.number().int().nullable(),
  exigePrestador: z.boolean(),
  ordemDeExibicao: z.number().int(),
})

type ExtraDaVitrine = z.infer<typeof extraDaVitrineSchema>

/** A vitrine não é paginada: é catálogo de tela, não extrato. */
const vitrineDeExtrasSchema = z.object({ itens: z.array(extraDaVitrineSchema) })

type VitrineDeExtras = z.infer<typeof vitrineDeExtrasSchema>

const CHAVE_DA_VITRINE_DE_EXTRAS = 'vitrine-de-extras'

function useExtrasDaVitrine(): UseQueryResult<VitrineDeExtras> {
  return useQuery({
    queryKey: [CHAVE_DA_VITRINE_DE_EXTRAS],
    queryFn: ({ signal }) => apiGet(ROTAS_DE_VITRINE.extras, vitrineDeExtrasSchema, { signal }),
  })
}

// ---------------------------------------------------------------------------
// Constantes e apoio
// ---------------------------------------------------------------------------

/** Só quem pode assinar contrato hoje entra na cotação em nome de terceiro. */
const CONTAS_ATIVAS: FiltrosDeContas = {
  pagina: 1,
  tamanho: TAMANHO_MAXIMO_DE_PAGINA,
  busca: '',
  tipo: '',
  status: 'ATIVA',
  regimeDeFaturamento: '',
}

/**
 * Cadastro faltando é `danger`; mercado cheio é `warning`.
 *
 * A distinção não é estética: "não tem tarifa para esta faixa" se resolve em
 * dois minutos no cadastro, e escondido atrás de um aviso genérico ficaria meses
 * sem ninguém notar. "Toda a frota está ocupada" é informação de negócio — não
 * há nada errado a consertar.
 */
const VARIANTE_DA_INDISPONIBILIDADE = {
  SEM_FROTA: 'warning',
  SEM_UNIDADE_LIVRE: 'warning',
  SEM_TABELA_DE_PRECO: 'danger',
  SEM_TARIFA: 'danger',
} as const satisfies Readonly<Record<MotivoDeIndisponibilidade, unknown>>

/** `sáb 15/03` — o rótulo do dia no detalhe. */
function rotuloDoDia(data: string): string {
  return `${rotuloDosDiasDaSemana([dayOfWeekFromKey(data)])} ${formatarData(data).slice(0, 5)}`
}

/**
 * A conferência que dá o direito de exibir o total.
 *
 * `breakdownFecha` do `@locaveri/shared` faz a mesma pergunta sobre a cotação do
 * MOTOR (dinheiro em `bigint`); aqui a cotação chegou pelo fio, com o dinheiro
 * em string de centavos, e por isso a soma é refeita sobre `fromWire`. Valor
 * ilegível cai no `catch` e vale como "não fecha" — string estranha no campo de
 * dinheiro é exatamente o caso em que a tela não pode afirmar nada.
 */
function breakdownFecha(cotacao: {
  readonly linhas: readonly { readonly valor: string }[]
  readonly total: string
}): boolean {
  try {
    const soma: Money = sumAll(cotacao.linhas.map((linha) => fromWire(linha.valor)))

    return soma === fromWire(cotacao.total)
  } catch {
    return false
  }
}

/** O hash inteiro cabe no `title`; na tela, os doze primeiros bastam para conferir. */
function hashCurto(hash: string): string {
  return hash.slice(0, 12)
}

// ---------------------------------------------------------------------------
// Lista de espera
// ---------------------------------------------------------------------------

const CAMPOS_DA_ESPERA = ['nome', 'email', 'telefone', 'observacoes'] as const

interface FormularioDaEspera {
  nome: string
  email: string
  telefone: string
  observacoes: string
}

/** O que a tela precisa saber para registrar a intenção do cliente. */
interface PedidoDeEspera {
  readonly categoryId: string
  readonly categoriaNome: string
  /** Instantes já validados, os mesmos que foram cotados. */
  readonly retirada: string
  readonly devolucao: string
  /** `''` quando a cotação não foi feita em nome de uma conta. */
  readonly accountId: string
}

function valoresIniciaisDaEspera(): FormularioDaEspera {
  return { nome: '', email: '', telefone: '', observacoes: '' }
}

/**
 * "Não tinha carro" sem deixar rastro é demanda que some do relatório.
 *
 * Registrar a intenção custa quase nada e é o dado que mais vale para decidir
 * compra de frota — Gap 12 do roadmap. O contato é obrigatório porque a fila
 * existe para alguém LIGAR de volta: sem conta, sem e-mail e sem telefone, o
 * registro não serve para nada, e é o que o schema recusa.
 */
function DialogoDeListaDeEspera({
  pedido,
  aoFechar,
}: {
  readonly pedido: PedidoDeEspera | null
  readonly aoFechar: () => void
}): ReactElement {
  const [erroGeral, setErroGeral] = useState<string | null>(null)
  const registrar = useCriarItemDeWaitlist()

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    reset,
    watch,
    formState: { errors, isSubmitted },
  } = useForm<FormularioDaEspera>({ defaultValues: valoresIniciaisDaEspera() })

  // O diálogo é montado uma vez e reaproveitado: sem este efeito, o contato de
  // um cliente reapareceria no registro do próximo.
  useEffect(() => {
    if (pedido !== null) {
      setErroGeral(null)
      reset(valoresIniciaisDaEspera())
    }
  }, [pedido, reset])

  const telefoneDigitado = watch('telefone')
  const ocupado = registrar.isPending

  const aoEnviar = handleSubmit((valores) => {
    if (pedido === null) {
      return
    }

    setErroGeral(null)

    const nome = valores.nome.trim()
    const email = valores.email.trim()
    const telefone = apenasDigitos(valores.telefone)
    const observacoes = valores.observacoes.trim()

    const entrada = {
      categoryId: pedido.categoryId,
      retirada: pedido.retirada,
      devolucao: pedido.devolucao,
      ...(pedido.accountId.length > 0 ? { accountId: pedido.accountId } : {}),
      ...(nome.length > 0 ? { nome } : {}),
      ...(email.length > 0 ? { email } : {}),
      ...(telefone.length > 0 ? { telefone } : {}),
      ...(observacoes.length > 0 ? { observacoes } : {}),
    }

    const validado = criarItemDeWaitlistSchema.safeParse(entrada)

    if (!validado.success) {
      setErroGeral(aplicarErrosDoZod(validado.error, setError, CAMPOS_DA_ESPERA))
      return
    }

    // `entrada`, e não `validado.data`: o schema transforma os instantes em
    // `Date`, e o que a API espera no corpo é a string ISO.
    registrar.mutate(entrada, {
      onSuccess: () => {
        toast.success('Interesse registrado', {
          description: `${pedido.categoriaNome} · quando abrir vaga no período, é esta fila que a operação liga.`,
        })
        aoFechar()
      },
      onError: (causa: unknown) => {
        setErroGeral(aplicarErrosDaApi(causa, setError, CAMPOS_DA_ESPERA))
      },
    })
  })

  return (
    <Dialog
      open={pedido !== null}
      onOpenChange={(proximo) => {
        if (!proximo) {
          aoFechar()
        }
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Registrar na lista de espera</DialogTitle>
          <DialogDescription>
            {pedido === null
              ? ''
              : `${pedido.categoriaNome} · ${formatarDataHora(pedido.retirada)} a ${formatarDataHora(pedido.devolucao)}`}
          </DialogDescription>
        </DialogHeader>

        <form
          noValidate
          onSubmit={(evento) => {
            void aoEnviar(evento)
          }}
          className="space-y-4"
        >
          {erroGeral === null ? null : (
            <Alert variant="danger">
              <AlertDescription>{erroGeral}</AlertDescription>
            </Alert>
          )}

          <Campo rotulo="Nome" erro={errors.nome?.message}>
            {(campo) => <Input {...campo} {...register('nome')} disabled={ocupado} />}
          </Campo>

          <div className="grid gap-4 sm:grid-cols-2">
            <Campo
              rotulo="E-mail"
              erro={errors.email?.message}
              dica="E-mail ou telefone — ao menos um contato."
            >
              {(campo) => (
                <Input {...campo} {...register('email')} type="email" disabled={ocupado} />
              )}
            </Campo>

            <Campo rotulo="Telefone" erro={errors.telefone?.message}>
              {(campo) => (
                <Input
                  {...campo}
                  {...register('telefone')}
                  value={telefoneDigitado}
                  onChange={(evento) => {
                    // `shouldValidate` só depois do primeiro envio: validar a
                    // cada tecla acusaria telefone inválido no terceiro dígito.
                    setValue('telefone', mascararTelefoneProgressivo(evento.target.value), {
                      shouldValidate: isSubmitted,
                    })
                  }}
                  inputMode="tel"
                  placeholder="(11) 99999-9999"
                  className="tabular-money"
                  disabled={ocupado}
                />
              )}
            </Campo>
          </div>

          <Campo
            rotulo="Observações"
            erro={errors.observacoes?.message}
            dica="Flexibilidade de data, categoria alternativa aceita, urgência."
          >
            {(campo) => (
              <Textarea {...campo} {...register('observacoes')} rows={3} disabled={ocupado} />
            )}
          </Campo>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={aoFechar} disabled={ocupado}>
              Cancelar
            </Button>
            <Button type="submit" disabled={ocupado}>
              {ocupado ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
              Registrar interesse
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Seletor de conta
// ---------------------------------------------------------------------------

/**
 * A conta em nome de quem se cota — é o que puxa a tabela negociada.
 *
 * Componente próprio para que a consulta só aconteça quando o campo existe: quem
 * não tem `account.read` não vê o seletor e não gasta um 403 para descobrir.
 */
function SeletorDeConta({
  valor,
  aoMudar,
}: {
  readonly valor: string
  /**
   * Devolve a CONTA junto do id, e não só o id.
   *
   * A tela precisa do nome e do telefone para montar a mensagem do WhatsApp
   * (Gap 13). Buscá-los de novo lá em cima seria uma segunda consulta ao mesmo
   * recurso — e, pior, uma que dispararia 403 para quem não tem `account.read`,
   * já que hook não se chama dentro de `if`.
   */
  readonly aoMudar: (accountId: string, conta: Conta | null) => void
}): ReactElement {
  const consulta = useContas(CONTAS_ATIVAS)
  const contas = consulta.data?.itens ?? []

  return (
    <div className="space-y-1.5">
      <Label htmlFor="cotacao-conta">Cliente</Label>
      <Select
        id="cotacao-conta"
        value={valor}
        onChange={(evento) => {
          const escolhido = evento.target.value
          aoMudar(escolhido, contas.find((conta) => conta.id === escolhido) ?? null)
        }}
        disabled={consulta.isPending || consulta.isError}
      >
        <option value="">{consulta.isPending ? 'Carregando…' : 'Tabela padrão (sem conta)'}</option>
        {contas.map((conta) => (
          <option key={conta.id} value={conta.id}>
            {nomeDaConta(conta)}
          </option>
        ))}
      </Select>
      {consulta.isError ? (
        <p className="text-xs text-danger-fg">
          Não foi possível carregar os clientes — a cotação sai pela tabela padrão.
        </p>
      ) : consulta.isPending ? (
        <p className="text-xs text-fg-muted">Carregando os clientes…</p>
      ) : contas.length === 0 ? (
        <p className="text-xs text-fg-muted">
          Nenhuma conta ativa cadastrada — toda cotação sai pela tabela padrão.
        </p>
      ) : (
        <p className="text-xs text-fg-muted">Cliente corporativo pode ter tabela negociada.</p>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// O resultado de uma categoria
// ---------------------------------------------------------------------------

interface CartaoDaCategoriaProps {
  readonly item: CategoriaCotada
  readonly envelope: ResultadoDaCotacao
  readonly podeRegistrarEspera: boolean
  readonly aoRegistrarEspera: (item: CategoriaCotada) => void
  /**
   * A RESERVA ASSISTIDA (Gap 13) mora aqui, e não numa tela própria.
   *
   * O atendente já está nesta tela, com o cliente no telefone, olhando o total
   * desta categoria. Mandá-lo para outro lugar para fechar seria pedir período
   * e categoria de novo — a porta do clássico "cotei X, reservei Y" — e uma
   * tela a mais para quem pediu MENOS telas.
   *
   * Duas saídas porque são dois clientes diferentes: quem quer resolver na
   * conversa recebe o LINK e fecha sozinho no celular; quem não quer app nenhum
   * é fechado aqui mesmo, gerando o MESMO registro.
   */
  readonly podeFecharReserva: boolean
  readonly aoFecharReserva: (item: CategoriaCotada) => void
  readonly aoMandarLink: (item: CategoriaCotada) => void
  /** Sem conta escolhida não há em nome de quem reservar — só o link sai. */
  readonly temConta: boolean
}

/**
 * Uma categoria cotada, do total ao dia a dia.
 *
 * A ordem é a da confiança: primeiro o total (o número que interessa), depois de
 * onde ele veio (o breakdown, que SOMA para ele), depois o detalhe por dia (que
 * explica cada diária) e por fim o que foi recusado. Inverter isso faria o
 * gestor rolar a tela para achar o preço.
 */
function CartaoDaCategoria({
  item,
  envelope,
  podeRegistrarEspera,
  aoRegistrarEspera,
  podeFecharReserva,
  aoFecharReserva,
  aoMandarLink,
  temConta,
}: CartaoDaCategoriaProps): ReactElement {
  const cotacao = item.cotacao
  const fecha = cotacao === null ? false : breakdownFecha(cotacao)

  return (
    <article className="flex flex-col gap-4 rounded-lg border border-border bg-surface p-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate text-base font-semibold text-foreground">{item.nome}</h3>
          <p className="mt-1 text-xs tabular-money text-fg-secondary">
            {disponibilidadeEmTexto(item)} disponíveis
            {item.overbookingDisponivel > 0
              ? ` · +${String(item.overbookingDisponivel)} por overbooking`
              : ''}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {item.tabelaNegociada ? (
            <Badge variant="info">
              <Handshake aria-hidden="true" />
              Tabela negociada
            </Badge>
          ) : null}

          {item.emOverbooking ? (
            <Badge variant="warning">
              <TriangleAlert aria-hidden="true" />
              Só cabe em overbooking
            </Badge>
          ) : null}

          {cotacao === null ? (
            <Badge variant="neutral">
              <CircleSlash aria-hidden="true" />
              Sem preço
            </Badge>
          ) : (
            <BadgeDeStatusDaCotacao status={statusVigenteDaCotacao(cotacao)} />
          )}
        </div>
      </header>

      {item.disponivel || item.motivo === null ? null : (
        <Alert variant={VARIANTE_DA_INDISPONIBILIDADE[item.motivo]}>
          <AlertTitle>{ROTULO_DO_MOTIVO_DE_INDISPONIBILIDADE[item.motivo]}</AlertTitle>
          <AlertDescription>{ACAO_DO_MOTIVO_DE_INDISPONIBILIDADE[item.motivo]}</AlertDescription>
          {item.listaDeEspera && podeRegistrarEspera ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="mt-2"
              onClick={() => {
                aoRegistrarEspera(item)
              }}
            >
              <BellPlus aria-hidden="true" />
              Registrar na lista de espera
            </Button>
          ) : null}
        </Alert>
      )}

      {/* O período dá diárias mesmo sem preço — é o número que o cliente compara
          com a próxima data disponível. */}
      {cotacao === null ? (
        <p className="text-sm text-fg-secondary">
          O período daria{' '}
          <span className="tabular-money font-medium text-foreground">{item.diarias}</span>{' '}
          {item.diarias === 1 ? 'diária' : 'diárias'}, com tolerância de{' '}
          <span className="tabular-money">{item.toleranciaEmMinutos} min</span>.
        </p>
      ) : (
        <>
          {fecha ? (
            <dl className="grid gap-3 sm:grid-cols-4">
              <div>
                <dt className="text-xs text-fg-muted">Total</dt>
                {/* O único número amarelo do cartão: é onde o olho deve parar.
                    `text-brand` resolve por tema — amarelo puro como cor de
                    texto reprovaria o contraste no tema claro. */}
                <dd className="numeric-display tabular-money text-brand text-2xl">
                  {formatarMoedaDoWire(cotacao.total)}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-fg-muted">Diária média</dt>
                <dd className="numeric-display tabular-money text-lg text-foreground">
                  {formatarMoedaDoWire(cotacao.diariaMedia)}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-fg-muted">Diárias</dt>
                <dd className="numeric-display text-lg text-foreground">{cotacao.diarias}</dd>
                <dd className="text-xs text-fg-secondary">{ROTULO_DA_FAIXA[cotacao.faixa]}</dd>
              </div>
              <div>
                <dt className="text-xs text-fg-muted">Caução</dt>
                {/* `money-cell` alinha à direita, e num KPI ao lado de três
                    valores alinhados à esquerda isso lia como coluna torta. Em
                    KPI o par é `numeric-display` (peso) + `tabular-money`
                    (dígitos de largura fixa); a versão alinhada à direita fica
                    para as células de tabela abaixo. */}
                <dd className="numeric-display tabular-money text-lg text-foreground">
                  {formatarMoedaDoWire(cotacao.caucao)}
                </dd>
              </div>
            </dl>
          ) : (
            <Alert variant="danger">
              <AlertTitle>A soma das linhas não bate com o total</AlertTitle>
              <AlertDescription>
                Esta cotação não é confiável e por isso o valor não é exibido. Não passe este preço
                ao cliente: cote de novo e, se repetir, avise quem cuida das regras — o snapshot
                ficou inconsistente com o breakdown que o explica.
              </AlertDescription>
            </Alert>
          )}

          {/* ------------------------------------------------------------
              Fechar aqui, ou mandar o link — a ponte do WhatsApp (Gap 13)

              Logo DEPOIS dos números e ANTES do breakdown: é aqui que a decisão
              acontece. No fim do cartão, o atendente teria de rolar a tabela
              dia a dia com o cliente esperando no telefone.

              Some quando o breakdown não fecha: uma cotação cujas linhas não
              somam o total não vira reserva nem vai para o cliente.
              ------------------------------------------------------------ */}
          {fecha && item.disponivel ? (
            <div className="flex flex-wrap gap-2 border-t border-border pt-4">
              <Button
                type="button"
                onClick={() => {
                  aoMandarLink(item)
                }}
              >
                <Send aria-hidden="true" />
                Mandar link para o cliente
              </Button>

              {podeFecharReserva ? (
                <Button
                  type="button"
                  variant="secondary"
                  disabled={!temConta}
                  // Botão desabilitado sem explicação é botão quebrado.
                  title={
                    temConta ? undefined : 'Escolha a conta do cliente para reservar em nome dela.'
                  }
                  onClick={() => {
                    aoFecharReserva(item)
                  }}
                >
                  <CalendarCheck aria-hidden="true" />
                  Fechar aqui mesmo
                </Button>
              ) : null}
            </div>
          ) : null}

          {/* ------------------------------------------------------------
              Breakdown linha a linha
              ------------------------------------------------------------ */}
          <div>
            <h4 className="text-sm font-medium text-foreground">De onde vem o total</h4>
            <p className="mt-1 text-xs text-fg-muted">
              As linhas somam: desconto entra negativo, e não como valor a subtrair.
            </p>

            <div className="mt-2">
              <Table>
                <TableCaption className="sr-only">
                  Detalhamento da cotação de {item.nome}
                </TableCaption>
                <TableHeader>
                  <TableRow>
                    <TableHead>Linha</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead>Qtd.</TableHead>
                    {/* As células de valor são `money-cell` (alinhadas à
                        direita); cabeçalho à esquerda deixava o rótulo longe da
                        coluna de dígitos. */}
                    <TableHead className="text-right">Valor</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cotacao.linhas.map((linha, indice) => (
                    <TableRow key={`${linha.tipo}-${String(indice)}`}>
                      <TableCell>
                        <BadgeDeTipoDeLinha tipo={linha.tipo} />
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-foreground">{linha.descricao}</span>
                      </TableCell>
                      <TableCell>
                        <span className="tabular-money text-sm text-fg-secondary">
                          {linha.quantidade}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="money-cell block text-foreground">
                          {formatarMoedaDoWire(linha.valor)}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}

                  <TableRow>
                    <TableCell colSpan={3}>
                      <span className="text-sm font-medium text-foreground">Total</span>
                    </TableCell>
                    <TableCell>
                      <span className="money-cell block font-semibold text-foreground">
                        {fecha ? formatarMoedaDoWire(cotacao.total) : 'não confere'}
                      </span>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>

            <p className="mt-2 text-xs tabular-money text-fg-muted">
              Diárias {formatarMoedaDoWire(cotacao.subtotalDasDiarias)} · extras{' '}
              {formatarMoedaDoWire(cotacao.totalDeExtras)} · desconto{' '}
              {formatarMoedaDoWire(cotacao.desconto)}
            </p>
          </div>

          {/* ------------------------------------------------------------
              Dia a dia
              ------------------------------------------------------------ */}
          <div>
            <h4 className="text-sm font-medium text-foreground">Diária de cada dia</h4>
            <p className="mt-1 text-xs text-fg-muted">
              É aqui que o fim de semana e o feriado aparecem pesando — e onde dá para ver o teto ou
              o piso da categoria mordendo o valor.
            </p>

            <div className="mt-2">
              <Table>
                <TableCaption className="sr-only">
                  Diária dia a dia da cotação de {item.nome}
                </TableCaption>
                <TableHeader>
                  <TableRow>
                    <TableHead>Dia</TableHead>
                    <TableHead className="text-right">Base</TableHead>
                    <TableHead>Ajustes</TableHead>
                    <TableHead className="text-right">Diária</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cotacao.porDia.map((dia) => (
                    <TableRow key={dia.data}>
                      <TableCell>
                        <span className="whitespace-nowrap text-sm tabular-money text-foreground">
                          {rotuloDoDia(dia.data)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="money-cell block text-fg-secondary">
                          {formatarMoedaDoWire(dia.base)}
                        </span>
                      </TableCell>
                      <TableCell>
                        {dia.ajustes.length === 0 && !dia.limitado ? (
                          <span className="text-xs text-fg-muted">sem ajuste</span>
                        ) : (
                          <ul className="space-y-1">
                            {dia.ajustes.map((ajuste) => (
                              <li key={`${ajuste.origem}-${ajuste.regraId}`} className="text-xs">
                                <span className="text-fg-secondary">
                                  {ROTULO_DA_ORIGEM_DO_AJUSTE[ajuste.origem]}: {ajuste.nome}
                                </span>{' '}
                                <span className="tabular-money text-foreground">
                                  {formatarMoedaDoWire(ajuste.delta)}
                                </span>
                              </li>
                            ))}

                            {dia.limitado ? (
                              <li>
                                <Badge variant="warning">
                                  <Ruler aria-hidden="true" />
                                  {dia.deltaDoLimite.startsWith('-')
                                    ? 'Teto da categoria'
                                    : 'Piso da categoria'}{' '}
                                  {formatarMoedaDoWire(dia.deltaDoLimite)}
                                </Badge>
                              </li>
                            ) : null}
                          </ul>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="money-cell block text-foreground">
                          {formatarMoedaDoWire(dia.valor)}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          <p className="text-xs tabular-money text-fg-muted">
            Tolerância de {cotacao.toleranciaEmMinutos} min · cotação {hashCurto(cotacao.id)} · hash
            das regras <code title={cotacao.hashDasRegras}>{hashCurto(cotacao.hashDasRegras)}</code>{' '}
            · vale até {formatarDataHora(cotacao.expiraEm)}
          </p>
        </>
      )}

      {/* ----------------------------------------------------------------
          O que foi pedido e não entrou
          ---------------------------------------------------------------- */}
      {item.extrasRecusados.length === 0 ? null : (
        <div className="rounded-lg border border-warning-fg/40 bg-warning/10 p-3">
          <p className="text-sm font-medium text-warning-fg">Extras que não entraram</p>
          <ul className="mt-2 space-y-1">
            {item.extrasRecusados.map((extra) => (
              <li key={extra.extraId} className="text-xs text-warning-fg">
                <span className="font-medium">{extra.nome ?? 'Extra desconhecido'}</span> ×
                <span className="tabular-money">{extra.quantidade}</span> —{' '}
                {ROTULO_DO_MOTIVO_DE_RECUSA_DE_EXTRA[extra.motivo]}: {extra.detalhe}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* O cupom valeu no geral, mas o total desta categoria não alcançou o
          piso — a única recusa que varia de categoria para categoria. */}
      {envelope.cupom !== null && envelope.cupom.aceito && !item.cupomAplicado ? (
        <Alert variant="info">
          <AlertDescription>
            O cupom {envelope.cupom.codigo} não entrou nesta categoria
            {envelope.cupom.valorMinimoDaReserva === null
              ? '.'
              : `: o total não alcançou o mínimo de ${formatarMoedaDoWire(envelope.cupom.valorMinimoDaReserva)}.`}
          </AlertDescription>
        </Alert>
      ) : null}
    </article>
  )
}

// ---------------------------------------------------------------------------
// Tela
// ---------------------------------------------------------------------------

export function SimuladorPage(): ReactElement {
  const podeCotar = usePermissao('quote.create')
  const podeVerContas = usePermissao('account.read')
  const podeRegistrarEspera = usePermissao('waitlist.create')
  const podeFecharReserva = usePermissao('reservation.create')
  const navegar = useNavigate()

  const [categoryId, setCategoryId] = useState('')
  const [retiradaDigitada, setRetiradaDigitada] = useState(() =>
    campoDeInstanteRelativo(1, '09:00'),
  )
  const [devolucaoDigitada, setDevolucaoDigitada] = useState(() =>
    campoDeInstanteRelativo(4, '09:00'),
  )
  const [retiradaLocalId, setRetiradaLocalId] = useState('')
  const [devolucaoLocalId, setDevolucaoLocalId] = useState('')
  const [accountId, setAccountId] = useState('')
  const [cupomDigitado, setCupomDigitado] = useState('')
  const [quantidades, setQuantidades] = useState<Readonly<Record<string, number>>>({})
  const [erroDoFormulario, setErroDoFormulario] = useState<string | null>(null)
  const [pedidoDeEspera, setPedidoDeEspera] = useState<PedidoDeEspera | null>(null)

  // A reserva assistida (Gap 13). A conta inteira, e não só o id: o nome e o
  // telefone montam a mensagem do WhatsApp.
  const [conta, setConta] = useState<Conta | null>(null)
  const [linkParaOCliente, setLinkParaOCliente] = useState<DadosDoLinkParaOCliente | null>(null)
  const [cotacaoParaFechar, setCotacaoParaFechar] = useState<CotacaoParaFechar | null>(null)

  const categorias = useCategoriasAtivas()
  const locais = useLocaisAtivos()
  const extras = useExtrasDaVitrine()
  const cotar = useCotar()

  const listaDeCategorias = categorias.data?.itens ?? []
  const listaDeLocais = locais.data?.itens ?? []
  const listaDeExtras = extras.data?.itens ?? []

  const retiradaIso = paraInstante(retiradaDigitada)
  const devolucaoIso = paraInstante(devolucaoDigitada)
  const erroDaJanela = erroDoPeriodo(retiradaIso ?? '', devolucaoIso ?? '')

  const extrasEscolhidos = useMemo<readonly ExtraDoPedido[]>(
    () =>
      Object.entries(quantidades)
        .filter(([, quantidade]) => quantidade > 0)
        .map(([extraId, quantidade]) => ({ extraId, quantidade })),
    [quantidades],
  )

  function ajustarQuantidade(extraId: string, quantidade: number): void {
    setQuantidades((anterior) => {
      if (quantidade > 0) {
        return { ...anterior, [extraId]: quantidade }
      }

      // Reconstrói SEM a chave: um extra desmarcado precisa sair do pedido, e
      // uma quantidade zero residual viajaria como `quantidade: 0` — que o
      // schema recusa, e com razão.
      return Object.fromEntries(Object.entries(anterior).filter(([chave]) => chave !== extraId))
    })
  }

  function pedirCotacao(): void {
    setErroDoFormulario(null)

    if (retiradaIso === null || devolucaoIso === null) {
      setErroDoFormulario('informe a retirada e a devolução')
      return
    }

    if (erroDaJanela !== null) {
      setErroDoFormulario(erroDaJanela)
      return
    }

    const cupom = normalizarCodigoDeCupom(cupomDigitado)

    if (cupom.length > 0) {
      const validado = codigoDeCupomSchema.safeParse(cupom)

      if (!validado.success) {
        setErroDoFormulario(validado.error.issues[0]?.message ?? 'código de cupom inválido')
        return
      }
    }

    const pedido: PedidoDeCotacao = {
      retirada: retiradaIso,
      devolucao: devolucaoIso,
      // Categoria ausente cota TODAS as ativas — a pergunta que o painel faz.
      ...(categoryId.length > 0 ? { categoryId } : {}),
      ...(retiradaLocalId.length > 0 ? { retiradaLocalId } : {}),
      ...(devolucaoLocalId.length > 0 ? { devolucaoLocalId } : {}),
      ...(accountId.length > 0 ? { accountId } : {}),
      ...(extrasEscolhidos.length > 0 ? { extras: extrasEscolhidos } : {}),
      ...(cupom.length > 0 ? { cupom } : {}),
    }

    cotar.mutate(pedido, {
      onError: (causa: unknown) => {
        toast.error('Não foi possível cotar', { description: errorMessage(causa) })
      },
    })
  }

  function abrirListaDeEspera(item: CategoriaCotada): void {
    const envelope = cotar.data

    if (envelope === undefined) {
      return
    }

    setPedidoDeEspera({
      categoryId: item.categoryId,
      categoriaNome: item.nome,
      // O período do ENVELOPE, e não o que está no formulário: entre a cotação e
      // o clique a pessoa pode ter mexido nos campos, e a fila tem que guardar
      // exatamente o período que ficou sem carro.
      retirada: envelope.retirada,
      devolucao: envelope.devolucao,
      accountId,
    })
  }

  /**
   * O período do ENVELOPE, em data e hora de PAREDE do ponto de retirada.
   *
   * Do envelope e não do formulário, pela razão do `abrirListaDeEspera`: entre
   * a cotação e o clique a pessoa pode ter mexido nos campos, e o link tem de
   * levar exatamente o período que foi cotado — senão o cliente abre e vê outro
   * preço, sem entender por quê.
   *
   * No fuso da RETIRADA (invariante nº 2), e não no do navegador: um atendente
   * atendendo de outro fuso mandaria a data errada, e a diária vira no fuso do
   * ponto.
   */
  function periodoDoEnvelope(envelope: ResultadoDaCotacao): {
    readonly data: string
    readonly hora: string
    readonly ate: string
    readonly horaAte: string
  } {
    const retirada = new Date(envelope.retirada)
    const devolucao = new Date(envelope.devolucao)

    return {
      data: localDayKey(retirada, envelope.timeZone),
      hora: formatInTimeZone(retirada, 'HH:mm', envelope.timeZone),
      ate: localDayKey(devolucao, envelope.timeZone),
      horaAte: formatInTimeZone(devolucao, 'HH:mm', envelope.timeZone),
    }
  }

  function abrirLink(item: CategoriaCotada): void {
    const envelope = cotar.data
    if (envelope === undefined || item.cotacao === null) {
      return
    }

    const periodo = periodoDoEnvelope(envelope)
    // O slug vem do CATÁLOGO: `CategoriaCotada` traz o uuid e o nome, e o link
    // usa slug porque é o identificador que a vitrine pública expõe — e o único
    // que um humano consegue ler numa conversa de WhatsApp.
    const slug = listaDeCategorias.find((categoria) => categoria.id === item.categoryId)?.slug

    setLinkParaOCliente({
      categoriaSlug: slug ?? null,
      categoriaNome: item.nome,
      retirada: periodo.data,
      devolucao: periodo.ate,
      horaDeRetirada: periodo.hora,
      horaDeDevolucao: periodo.horaAte,
      retiradaLocalId: retiradaLocalId.length > 0 ? retiradaLocalId : null,
      devolucaoLocalId: devolucaoLocalId.length > 0 ? devolucaoLocalId : null,
      extras: extrasEscolhidos.map((extra) => ({
        extraId: extra.extraId,
        quantidade: extra.quantidade,
      })),
      // Só o cupom ACEITO viaja: mandar um recusado faria o funil do cliente
      // tentar aplicá-lo e mostrar a recusa de novo, do nada.
      cupom: envelope.cupom?.aceito === true ? envelope.cupom.codigo : null,
      periodoEmTexto: `${formatarDataHora(envelope.retirada)} a ${formatarDataHora(envelope.devolucao)}`,
      // O total vai como TEXTO na mensagem, nunca como parâmetro do link: preço
      // sai do motor, e uma cotação vencida tem de recotar em vez de honrar um
      // número que viajou pela URL.
      totalEmTexto: formatarMoedaDoWire(item.cotacao.total),
      nomeDoCliente: conta === null ? null : nomeDaConta(conta),
      telefoneDoCliente: conta?.telefone ?? null,
    })
  }

  function abrirFechamento(item: CategoriaCotada): void {
    if (item.cotacao === null || accountId.length === 0) {
      return
    }

    setCotacaoParaFechar({
      quoteId: item.cotacao.id,
      accountId,
      categoriaNome: item.nome,
      total: item.cotacao.total,
      caucao: item.cotacao.caucao,
      diarias: item.cotacao.diarias,
    })
  }

  const resultado = cotar.data

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold tracking-tight">Cotar e fechar reserva</h1>
          <p className="max-w-prose text-sm text-fg-secondary">
            O preço vem do motor determinístico, com o detalhamento linha a linha e dia a dia — o
            painel não recalcula nada, mostra o que foi congelado na cotação. Cotada a categoria, dá
            para fechar a reserva aqui mesmo ou mandar o link para o cliente terminar no celular.
          </p>
        </div>
      </div>

      {/* Um seletor vazio se lê como "não há categoria cadastrada" e manda
          alguém cadastrar o que já existe. A falha precisa ter nome. */}
      {categorias.isError || locais.isError ? (
        <Alert variant="warning">
          <AlertTitle>Não foi possível carregar categoria e ponto de retirada</AlertTitle>
          <AlertDescription>
            {categorias.isError ? errorMessage(categorias.error) : errorMessage(locais.error)} Dá
            para cotar mesmo assim: sem categoria escolhida, o motor cota TODAS as ativas.
          </AlertDescription>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="mt-2"
            onClick={() => {
              if (categorias.isError) {
                void categorias.refetch()
              }

              if (locais.isError) {
                void locais.refetch()
              }
            }}
          >
            <RefreshCw aria-hidden="true" />
            Tentar de novo
          </Button>
        </Alert>
      ) : null}

      {/* ------------------------------------------------------------------
          A pergunta
          ------------------------------------------------------------------ */}
      <section aria-labelledby="titulo-do-pedido" className="flex flex-col gap-4">
        <h2 id="titulo-do-pedido" className="sr-only">
          Período, categoria e opcionais
        </h2>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <div className="space-y-1.5">
            <Label htmlFor="cotacao-categoria">Categoria</Label>
            <Select
              id="cotacao-categoria"
              value={categoryId}
              onChange={(evento) => {
                setCategoryId(evento.target.value)
              }}
              disabled={categorias.isPending}
            >
              <option value="">
                {categorias.isPending ? 'Carregando…' : 'Todas as categorias'}
              </option>
              {listaDeCategorias.map((categoria) => (
                <option key={categoria.id} value={categoria.id}>
                  {categoria.nome}
                </option>
              ))}
            </Select>
            <p className="text-xs text-fg-muted">
              O cliente aluga categoria; a placa é decidida na alocação.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cotacao-retirada">Retirada</Label>
            <Input
              id="cotacao-retirada"
              type="datetime-local"
              value={retiradaDigitada}
              onChange={(evento) => {
                setRetiradaDigitada(evento.target.value)
              }}
              className="tabular-money"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cotacao-devolucao">Devolução</Label>
            <Input
              id="cotacao-devolucao"
              type="datetime-local"
              value={devolucaoDigitada}
              onChange={(evento) => {
                setDevolucaoDigitada(evento.target.value)
              }}
              className="tabular-money"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cotacao-local-retirada">Ponto de retirada</Label>
            <Select
              id="cotacao-local-retirada"
              value={retiradaLocalId}
              onChange={(evento) => {
                setRetiradaLocalId(evento.target.value)
              }}
              disabled={locais.isPending}
            >
              <option value="">{locais.isPending ? 'Carregando…' : 'Qualquer ponto'}</option>
              {listaDeLocais.map((local) => (
                <option key={local.id} value={local.id}>
                  {local.nome}
                </option>
              ))}
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cotacao-local-devolucao">Ponto de devolução</Label>
            <Select
              id="cotacao-local-devolucao"
              value={devolucaoLocalId}
              onChange={(evento) => {
                setDevolucaoLocalId(evento.target.value)
              }}
              disabled={locais.isPending}
            >
              <option value="">{locais.isPending ? 'Carregando…' : 'Mesmo da retirada'}</option>
              {listaDeLocais.map((local) => (
                <option key={local.id} value={local.id}>
                  {local.nome}
                </option>
              ))}
            </Select>
          </div>

          {podeVerContas ? (
            <SeletorDeConta
              valor={accountId}
              aoMudar={(escolhido, escolhida) => {
                setAccountId(escolhido)
                setConta(escolhida)
              }}
            />
          ) : null}

          <div className="space-y-1.5">
            <Label htmlFor="cotacao-cupom">Cupom</Label>
            <Input
              id="cotacao-cupom"
              value={cupomDigitado}
              onChange={(evento) => {
                setCupomDigitado(evento.target.value)
              }}
              placeholder="BEMVINDO15"
              autoComplete="off"
              className="uppercase"
            />
            <p className="text-xs text-fg-muted">
              O código é normalizado em maiúsculas antes de viajar.
            </p>
          </div>
        </div>

        {erroDaJanela === null ? null : (
          <Alert variant="warning">
            <AlertDescription>{erroDaJanela}</AlertDescription>
          </Alert>
        )}

        {/* --------------------------------------------------------------
            Extras
            -------------------------------------------------------------- */}
        <div className="space-y-2">
          <p className="text-sm font-medium text-foreground">Opcionais</p>

          {extras.isPending ? (
            <LoadingState rotulo="Carregando os extras…" linhas={1} />
          ) : extras.isError ? (
            <Alert variant="warning">
              <AlertTitle>Não foi possível carregar os extras</AlertTitle>
              <AlertDescription>
                Dá para cotar sem eles — o preço das diárias não depende desta lista.
              </AlertDescription>
            </Alert>
          ) : listaDeExtras.length === 0 ? (
            <p className="text-xs text-fg-muted">
              Nenhum extra na vitrine. Proteção, condutor adicional e motorista são cadastrados na
              tela de extras.
            </p>
          ) : (
            <ul className="grid gap-2 sm:grid-cols-2">
              {listaDeExtras.map((extra: ExtraDaVitrine) => {
                const quantidade = quantidades[extra.id] ?? 0
                const escolhido = quantidade > 0

                return (
                  <li
                    key={extra.id}
                    className="flex items-start justify-between gap-3 rounded-lg border border-border bg-surface p-3"
                  >
                    <label className="flex min-w-0 cursor-pointer items-start gap-2 text-sm">
                      <Checkbox
                        checked={escolhido}
                        onChange={(evento) => {
                          ajustarQuantidade(extra.id, evento.target.checked ? 1 : 0)
                        }}
                        className="mt-0.5"
                      />
                      <span className="min-w-0">
                        <span className="block truncate font-medium text-foreground">
                          {extra.nome}
                        </span>
                        <span className="block text-xs tabular-money text-fg-secondary">
                          {formatarMoedaDoWire(extra.valorUnitario)} ·{' '}
                          {ROTULO_DA_COBRANCA[extra.cobranca]}
                          {extra.exigePrestador ? ' · depende de prestador livre' : ''}
                        </span>
                      </span>
                    </label>

                    {escolhido ? (
                      <Input
                        type="number"
                        min={1}
                        {...(extra.quantidadeMaxima === null
                          ? {}
                          : { max: extra.quantidadeMaxima })}
                        inputMode="numeric"
                        aria-label={`Quantidade de ${extra.nome}`}
                        value={String(quantidade)}
                        onChange={(evento) => {
                          const proximo = Number(evento.target.value)

                          ajustarQuantidade(
                            extra.id,
                            Number.isInteger(proximo) && proximo > 0 ? proximo : 1,
                          )
                        }}
                        className="tabular-money w-20 shrink-0"
                      />
                    ) : null}
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        {erroDoFormulario === null ? null : (
          <Alert variant="danger">
            <AlertDescription>{erroDoFormulario}</AlertDescription>
          </Alert>
        )}

        {podeCotar ? (
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" onClick={pedirCotacao} disabled={cotar.isPending}>
              {cotar.isPending ? (
                <LoaderCircle className="animate-spin" aria-hidden="true" />
              ) : (
                <Calculator aria-hidden="true" />
              )}
              Cotar
            </Button>

            {extrasEscolhidos.length === 0 ? null : (
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setQuantidades({})
                }}
              >
                <Package aria-hidden="true" />
                Limpar opcionais
              </Button>
            )}
          </div>
        ) : (
          <Alert variant="neutral">
            <AlertDescription>
              Você pode consultar preços já cotados, mas não emitir cotação nova.
            </AlertDescription>
          </Alert>
        )}
      </section>

      {/* ------------------------------------------------------------------
          O resultado
          ------------------------------------------------------------------ */}
      <section aria-labelledby="titulo-do-resultado" className="flex flex-col gap-4">
        <h2 id="titulo-do-resultado" className="text-base font-semibold tracking-tight">
          Resultado
        </h2>

        {cotar.isPending ? (
          <LoadingState rotulo="Cotando…" linhas={2} />
        ) : cotar.isError ? (
          <ErrorState
            titulo="Não foi possível cotar"
            erro={cotar.error}
            aoTentarNovamente={pedirCotacao}
          />
        ) : resultado === undefined ? (
          <EmptyState
            icone={Calculator}
            titulo="Nenhuma cotação ainda"
            descricao="Escolha o período e clique em Cotar. Cada categoria cotável vira um snapshot congelado, com o hash das regras que produziram o número."
          />
        ) : (
          <>
            <div className="rounded-lg border border-border bg-surface p-4">
              <p className="text-sm tabular-money text-foreground">
                {formatarDataHora(resultado.retirada)} → {formatarDataHora(resultado.devolucao)}
              </p>
              <p className="mt-1 text-xs tabular-money text-fg-muted">
                Fuso {resultado.timeZone} · avaliado em {formatarDataHora(resultado.avaliadoEm)} ·
                buffer de {resultado.bufferEmHoras} h · as cotações valem até{' '}
                {formatarDataHora(resultado.expiraEm)}
              </p>
              <p className="mt-1 text-xs text-fg-muted">
                O instante da avaliação é o que a regra de antecedência usou — sem ele, duas
                cotações do mesmo período com preços diferentes seriam inexplicáveis.
              </p>
            </div>

            {resultado.cupom === null ? null : resultado.cupom.aceito ? (
              <Alert variant="success">
                <AlertTitle>Cupom {resultado.cupom.codigo} aceito</AlertTitle>
                <AlertDescription>
                  {resultado.cupom.valorMinimoDaReserva === null
                    ? 'Sem valor mínimo de reserva.'
                    : `Vale a partir de ${formatarMoedaDoWire(resultado.cupom.valorMinimoDaReserva)} de reserva — categorias abaixo disso não recebem o desconto.`}
                </AlertDescription>
              </Alert>
            ) : (
              <Alert variant="warning">
                <AlertTitle>Cupom {resultado.cupom.codigo} recusado</AlertTitle>
                <AlertDescription>
                  {resultado.cupom.motivo === null
                    ? 'O cupom não pôde ser aplicado.'
                    : ROTULO_DO_MOTIVO_DE_RECUSA_DE_CUPOM[resultado.cupom.motivo]}
                </AlertDescription>
              </Alert>
            )}

            {resultado.itens.length === 0 ? (
              <EmptyState
                icone={CircleSlash}
                titulo="Nenhuma categoria neste recorte"
                descricao="Nenhuma categoria ativa foi cotada. Confira se há categoria cadastrada e ativa para o período."
              />
            ) : (
              <div className="flex flex-col gap-4">
                {resultado.itens.map((item) => (
                  <CartaoDaCategoria
                    key={item.categoryId}
                    item={item}
                    envelope={resultado}
                    podeRegistrarEspera={podeRegistrarEspera}
                    aoRegistrarEspera={abrirListaDeEspera}
                    podeFecharReserva={podeFecharReserva}
                    aoFecharReserva={abrirFechamento}
                    aoMandarLink={abrirLink}
                    temConta={accountId.length > 0}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </section>

      <DialogoDeLinkParaOCliente
        dados={linkParaOCliente}
        aoFechar={() => {
          setLinkParaOCliente(null)
        }}
      />

      {podeFecharReserva ? (
        <DialogoDeFecharReserva
          cotacao={cotacaoParaFechar}
          aoFechar={() => {
            setCotacaoParaFechar(null)
          }}
          aoCriar={(reservaId) => {
            void navegar({ to: '/reservas/$reservaId', params: { reservaId } })
          }}
        />
      ) : null}

      {podeRegistrarEspera ? (
        <DialogoDeListaDeEspera
          pedido={pedidoDeEspera}
          aoFechar={() => {
            setPedidoDeEspera(null)
          }}
        />
      ) : null}
    </div>
  )
}
